import cn from 'classnames';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { OrganizationStaffRoleType } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Icon, Modal } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { PERMISSIONS, PermissionType } from '@/entities/organization';
import {
  permissionDescriptions,
  permissionOwners,
  PermissionStatuses,
} from '@/entities/organization/config/i18n';

import styles from './PermissionsModal.module.scss';

export type PermissionsModalProps = {
  className?: string;
};

export const PermissionsModal: FC<PermissionsModalProps> = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { visible } = useAppSelector((state) => state.modal.PermissionsModal);
  const { isMobile } = useMedia();

  const validPermissionOwners = Object.values(permissionOwners).filter(
    (permissionOwner) =>
      permissionOwner !==
      permissionOwners[
        OrganizationStaffRoleType.OrganizationStaffRoleType_InvalidValue
      ],
  );

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.PermissionsModal));
  }, []);

  return (
    <Modal
      data-component="permissions-modal"
      title={formatMessage({
        id: 'permissionsModal.title',
        defaultMessage: 'Access levels',
      })}
      isOpen={visible}
      onCancel={handleClose}
      className={styles.modal}
      containerClassName={styles.modalContainer}
      borderless
      hideFooter
    >
      <p className={cn(styles.subTitle, 'p2')}>
        <FormattedMessage
          id="PermissionsModal.subtitle"
          defaultMessage="Access levels you can assign to team members"
        />
      </p>
      <div className={styles.headingRow}>
        <p className={isMobile ? 'p3' : 'p2'}>
          <FormattedMessage
            id="permissionsModal.title"
            defaultMessage="Access levels"
          />
        </p>
        {validPermissionOwners.map((validPermissionOwner) => (
          <p key={validPermissionOwner.id} className={isMobile ? 'p3' : 'p2'}>
            {formatMessage(validPermissionOwner)}
          </p>
        ))}
      </div>
      {Object.values(permissionDescriptions).map((status) => (
        <div key={status.id} className={styles.row}>
          <p className={isMobile ? 'p3' : 'p2'}>{formatMessage(status)}</p>
          {/* TODO:[h/3] should be refactored after creating enum for permissions on the backend */}
          {(Object.keys(PERMISSIONS) as PermissionType[]).map((item) => (
            <p key={item}>
              {PERMISSIONS[item]?.includes(
                status.defaultMessage as PermissionStatuses,
              ) ? (
                <Icon size={24} className={styles.iconPlus} name="plus" />
              ) : (
                <Icon size={24} className={styles.iconMinus} name="minus" />
              )}
            </p>
          ))}
        </div>
      ))}
    </Modal>
  );
};
